import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Save, Send, CheckCircle2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { useDocumentStore } from '../../stores/documentStore';
import { useAssessmentDocStore } from '../../stores/assessmentDocStore';
import { MathUtils } from '../math/MathUtils';
import { InlineMathModal } from '../math/InlineMathField';
import AssessmentDocStartScreen from './AssessmentDocStartScreen';
import logo from '../../assets/images/logo.png';

const formatTime = (seconds) => {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const inferResponseType = (block) => {
  const explicit = block.getAttribute('data-response-type');
  if (explicit) return explicit;
  const title = block.querySelector('.response-area-preview-title')?.textContent?.toLowerCase() || '';
  if (title.includes('multiple choice')) return 'multiple-choice';
  if (title.includes('multi select')) return 'multi-select';
  if (title.includes('paragraph')) return 'paragraph';
  if (title.includes('image')) return 'image-upload';
  return 'short-answer';
};

const buildResponseConfigFromBlock = (block) => {
  const type = inferResponseType(block);
  const options = Array.from(block.querySelectorAll('.response-area-preview-text'))
    .map((node) => node.textContent?.trim())
    .filter(Boolean);
  const points = Number(block.getAttribute('data-response-points') || 1);
  const label = block.getAttribute('data-response-label')
    || block.querySelector('.response-area-preview-label')?.textContent?.trim()
    || '';

  return {
    type,
    label,
    required: block.getAttribute('data-response-required') !== 'false',
    options: (type === 'multiple-choice' || type === 'multi-select') ? options : null,
    points: Number.isFinite(points) && points > 0 ? points : 1,
  };
};

const normalizeLatexPreview = (content = '') =>
  String(content || '').replace(/\\degree\b/g, '^\\circ');

const getModelAnswers = (config) => {
  if (!config) return [];
  if (config.type === 'multiple-choice') {
    return config.correctAnswer ? [config.correctAnswer] : [];
  }
  if (Array.isArray(config.correctAnswers)) {
    return config.correctAnswers.filter(Boolean);
  }
  if (config.correctAnswer) {
    return [config.correctAnswer];
  }
  return [];
};

const hasAnsweredResponse = (config, value) => {
  if (config?.type === 'multi-select') return Array.isArray(value) && value.length > 0;
  if (config?.type === 'image-upload') return Boolean(value);
  return String(value ?? '').trim().length > 0;
};

const renderNativeResponseControl = ({
  host,
  blockId,
  config,
  value,
  disabled,
  enableModelAnswerToggle,
  canAccessModelAnswers,
  modelAnswerLockedMessage,
  onChange,
  onOpenMath,
}) => {
  host.innerHTML = '';
  host.setAttribute('data-response-learner-active', 'true');

  const wrapper = document.createElement('div');
  wrapper.className = 'space-y-2';
  const modelAnswers = getModelAnswers(config);
  let modelAnswerVisible = false;

  const appendModelAnswerPanel = async () => {
    const existing = wrapper.querySelector('[data-model-answer-panel="true"]');
    if (existing) existing.remove();
    if (!modelAnswerVisible || !canAccessModelAnswers || !hasAnsweredResponse(config, getCurrentValue())) {
      return;
    }

    const modelPanel = document.createElement('div');
    modelPanel.className = 'mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2';
    modelPanel.setAttribute('data-model-answer-panel', 'true');

    const modelLabel = document.createElement('div');
    modelLabel.className = 'text-[11px] uppercase tracking-wide text-emerald-700 mb-1';
    modelLabel.textContent = 'Model Answer';
    modelPanel.appendChild(modelLabel);

    modelAnswers.forEach((answer) => {
      const answerRow = document.createElement('div');
      answerRow.className = 'text-sm text-emerald-900 markdown-content';
      modelPanel.appendChild(answerRow);
      MathUtils.processContentWithMarkdown(normalizeLatexPreview(answer || ''))
        .then((html) => {
          answerRow.innerHTML = html;
          return MathUtils.renderMath(answerRow);
        })
        .catch((error) => {
          console.warn('Model answer render failed:', error);
          answerRow.innerHTML = String(answer || '').replace(/\n/g, '<br>');
        });
    });

    wrapper.appendChild(modelPanel);
  };

  let currentValueRef = value;
  const getCurrentValue = () => currentValueRef;
  let modelAnswerButton = null;
  let modelAnswerHint = null;
  const refreshModelAnswerState = (nextValue) => {
    currentValueRef = nextValue;
    const answered = hasAnsweredResponse(config, nextValue);
    if (modelAnswerButton) {
      modelAnswerButton.disabled = !canAccessModelAnswers || !answered;
      modelAnswerButton.title = !canAccessModelAnswers
        ? modelAnswerLockedMessage || 'Sign in to reveal model answers'
        : answered
          ? 'Toggle model answer'
          : 'Provide a response to unlock the model answer';
      modelAnswerButton.textContent = modelAnswerVisible ? 'Hide Model Answer' : 'Show Model Answer';
    }

    if (modelAnswerHint) {
      if (!canAccessModelAnswers) {
        modelAnswerHint.textContent = modelAnswerLockedMessage || 'Sign in to reveal model answers.';
        modelAnswerHint.className = 'mt-1 text-xs text-amber-700';
      } else if (!answered) {
        modelAnswerHint.textContent = 'Answer this response to unlock the model answer.';
        modelAnswerHint.className = 'mt-1 text-xs text-gray-500';
      } else if (modelAnswers.length === 0) {
        modelAnswerHint.textContent = 'No model answer is configured for this response.';
        modelAnswerHint.className = 'mt-1 text-xs text-gray-500';
      } else {
        modelAnswerHint.textContent = '';
      }
    }

    if (!canAccessModelAnswers || !answered) {
      modelAnswerVisible = false;
    }
    appendModelAnswerPanel();
  };

  const label = document.createElement('div');
  label.className = 'text-sm font-medium text-gray-700 mb-1';
  label.textContent = config.label || '';
  if (config.label) wrapper.appendChild(label);

  if (config.type === 'multiple-choice') {
    (config.options || []).filter(Boolean).forEach((option, index) => {
      const optionLabel = document.createElement('label');
      optionLabel.className = 'flex items-center gap-2 cursor-pointer';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `ra-${blockId}`;
      input.value = option;
      input.checked = value === option;
      input.disabled = disabled;
      input.className = 'text-sigma-600 focus:ring-sigma-500';
      input.addEventListener('change', () => {
        if (input.checked) {
          onChange(option);
          refreshModelAnswerState(option);
        }
      });

      const text = document.createElement('span');
      text.className = 'text-sm text-gray-800';
      text.textContent = option || `Option ${index + 1}`;

      optionLabel.appendChild(input);
      optionLabel.appendChild(text);
      wrapper.appendChild(optionLabel);
    });
  } else if (config.type === 'multi-select') {
    const selectedValues = Array.isArray(value) ? value : [];
    (config.options || []).filter(Boolean).forEach((option, index) => {
      const optionLabel = document.createElement('label');
      optionLabel.className = 'flex items-center gap-2 cursor-pointer';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = option;
      input.checked = selectedValues.includes(option);
      input.disabled = disabled;
      input.className = 'rounded border-gray-300 text-sigma-600 focus:ring-sigma-500';
      input.addEventListener('change', () => {
        const inputs = Array.from(wrapper.querySelectorAll('input[type="checkbox"]'));
        const nextValue = inputs.filter((item) => item.checked).map((item) => item.value);
        onChange(nextValue);
        refreshModelAnswerState(nextValue);
      });

      const text = document.createElement('span');
      text.className = 'text-sm text-gray-800';
      text.textContent = option || `Option ${index + 1}`;

      optionLabel.appendChild(input);
      optionLabel.appendChild(text);
      wrapper.appendChild(optionLabel);
    });
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = typeof value === 'string' ? value : '';
    textarea.placeholder = 'Type your answer...';
    textarea.rows = config.type === 'paragraph' ? 4 : 1;
    textarea.disabled = disabled;
    textarea.className = [
      'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#32289a]',
      'text-sm focus:ring-sigma-500 focus:border-sigma-500',
      config.type === 'paragraph' ? 'resize-y' : 'resize-none',
      disabled ? 'bg-gray-50' : '',
    ].join(' ');
    wrapper.appendChild(textarea);

    let previewVisible = false;
    const previewPanel = document.createElement('div');
    previewPanel.className = 'hidden mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2';

    const previewLabel = document.createElement('div');
    previewLabel.className = 'text-[11px] uppercase tracking-wide text-gray-400 mb-1';
    previewLabel.textContent = 'Preview';

    const previewBody = document.createElement('div');
    previewBody.className = 'text-sm text-gray-800 whitespace-pre-wrap';

    previewPanel.appendChild(previewLabel);
    previewPanel.appendChild(previewBody);

    const renderPreview = () => {
      previewBody.textContent = MathUtils.processMathExpressions(normalizeLatexPreview(textarea.value || ''));
      MathUtils.renderMath(previewBody).catch((error) => {
        console.warn('Response preview math render failed:', error);
      });
    };

    textarea.addEventListener('input', () => {
      onChange(textarea.value);
      refreshModelAnswerState(textarea.value);
      if (previewVisible) renderPreview();
    });

    if (!disabled) {
      textarea.addEventListener('keydown', (event) => {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm') {
          event.preventDefault();
          onOpenMath?.(textarea, onChange);
        }
      });

      const actions = document.createElement('div');
      actions.className = 'flex justify-end gap-4 mt-1';

      const previewButton = document.createElement('button');
      previewButton.type = 'button';
      previewButton.className = 'text-xs text-gray-500 flex items-center hover:text-gray-700';
      previewButton.textContent = 'Preview Math';
      previewButton.addEventListener('click', () => {
        previewVisible = !previewVisible;
        previewPanel.classList.toggle('hidden', !previewVisible);
        previewButton.textContent = previewVisible ? 'Hide Preview' : 'Preview Math';
        if (previewVisible) renderPreview();
      });

      const mathButton = document.createElement('button');
      mathButton.type = 'button';
      mathButton.className = 'text-xs text-gray-400 flex items-center hover:text-sigma-700';
      mathButton.title = 'Insert mathematical expression (Ctrl+M)';
      mathButton.innerHTML = '<span class="mr-1 text-xs font-serif italic">S</span> Insert Math (Ctrl+M)';
      mathButton.addEventListener('click', () => onOpenMath?.(textarea, onChange));

      actions.appendChild(previewButton);
      actions.appendChild(mathButton);
      wrapper.appendChild(actions);
    }

    wrapper.appendChild(previewPanel);
  }

  if (enableModelAnswerToggle) {
    const modelAnswerActions = document.createElement('div');
    modelAnswerActions.className = 'mt-2';

    if (modelAnswers.length > 0) {
      modelAnswerButton = document.createElement('button');
      modelAnswerButton.type = 'button';
      modelAnswerButton.className = 'inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400';
      modelAnswerButton.addEventListener('click', () => {
        modelAnswerVisible = !modelAnswerVisible;
        refreshModelAnswerState(getCurrentValue());
      });
      modelAnswerActions.appendChild(modelAnswerButton);
    }

    modelAnswerHint = document.createElement('p');
    modelAnswerActions.appendChild(modelAnswerHint);
    wrapper.appendChild(modelAnswerActions);
    refreshModelAnswerState(getCurrentValue());
  }

  host.appendChild(wrapper);
};

const AssessmentPages = React.memo(({ pages, pageContainerRef }) => (
  <div ref={pageContainerRef} className="mx-auto space-y-6" style={{ maxWidth: '210mm' }}>
    {pages.map((pageHtml, index) => (
      <div
        key={`take-page-${index}`}
        className="bg-white shadow-lg rounded border border-gray-200"
        style={{
          width: '210mm',
          minHeight: '297mm',
          padding: '25.4mm',
          fontSize: '14px',
          lineHeight: '1.6',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
        dangerouslySetInnerHTML={{ __html: pageHtml || '' }}
      />
    ))}
  </div>
));

const AssessmentDocTaking = ({ documentId }) => {
  const navigate = useNavigate();
  const { user, userData, isAdmin } = useAuthStore();
  const { currentDocument, loadDocument } = useDocumentStore();
  const {
    currentSubmission,
    responses,
    timeRemainingSeconds,
    saveStatus,
    lastSavedAt,
    isStarted,
    isSubmitted,
    isLoading,
    loadSubmission,
    startAssessment,
    updateResponse,
    saveResponses,
    submitAssessment,
    tickTimer,
    reset,
  } = useAssessmentDocStore();

  const pageContainerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const autoStartedRef = useRef(false);
  const latestResponsesRef = useRef(responses);
  const mathTargetRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const [submissionLoadComplete, setSubmissionLoadComplete] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [mathModalState, setMathModalState] = useState({
    isOpen: false,
    initialLatex: '',
    anchorRect: null,
  });

  const assessment = currentDocument?.assessment;
  const isSelfPractice = assessment?.category === 'informal' && assessment?.informalType === 'self-practice';
  const responseAreas = useMemo(
    () => assessment?.responseAreas || {},
    [assessment?.responseAreas]
  );
  const responseAreaOrder = useMemo(
    () => (Array.isArray(assessment?.responseAreaOrder) ? assessment.responseAreaOrder : []),
    [assessment?.responseAreaOrder]
  );
  const pages = useMemo(
    () => (Array.isArray(currentDocument?.pages) && currentDocument.pages.length > 0
      ? currentDocument.pages
      : ['']),
    [currentDocument?.pages]
  );
  const isSignedIn = Boolean(user?.uid);
  const canShowModelAnswerToggle = isSelfPractice;
  const modelAnswerHelperText = !isSignedIn
    ? 'Sign in to reveal model answers after answering each response.'
    : 'Model answers can be revealed per response after you answer it.';

  const isUserAdmin = isAdmin();

  // Load document and existing submission
  useEffect(() => {
    reset();
    setLoadError(null);
    setSubmissionLoadComplete(false);
    autoStartedRef.current = false;
    if (!documentId || !user?.uid) return;

    loadDocument(documentId, { currentUserId: user.uid, isAdmin: isUserAdmin })
      .then(() => loadSubmission(documentId, user.uid))
      .then(() => setSubmissionLoadComplete(true))
      .catch(() => {
        setSubmissionLoadComplete(true);
        setLoadError('Could not load assessment.');
      });
  }, [documentId, reset, loadDocument, loadSubmission, user?.uid, isUserAdmin]);

  // Auto-start informal assessments (self-practice and class-practice) without time limits
  // so response areas are immediately open — matching the shared-page self-practice experience.
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!currentDocument || !assessment?.enabled || !user?.uid) return;
    if (!submissionLoadComplete) return;
    if (isStarted || isSubmitted || isLoading || currentSubmission) return;
    // Formal and timed assessments go through the start screen
    if (assessment.category === 'formal' || assessment.timeLimit) return;

    autoStartedRef.current = true;
    startAssessment(
      documentId,
      {
        ...user,
        teacherAssignment: userData?.teacherAssignment || null,
        tutorAssignment: userData?.tutorAssignment || null,
      },
      assessment,
      {
        userId: currentDocument?.userId || null,
        ownerName: currentDocument?.ownerName || currentDocument?.userName || '',
        title: currentDocument?.title || currentDocument?.name || '',
        name: currentDocument?.name || currentDocument?.title || '',
      }
    ).catch((err) => {
      console.error('Auto-start assessment failed:', err);
      autoStartedRef.current = false;
    });
  }, [currentDocument?.id, assessment, submissionLoadComplete, isStarted, isSubmitted, isLoading, currentSubmission, user?.uid, documentId, startAssessment, user, userData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResponseChange = useCallback((blockId, value) => {
    updateResponse(blockId, value);
  }, [updateResponse]);

  const handleOpenMathEditor = useCallback((textarea, onChange) => {
    if (!textarea || textarea.disabled) return;

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || start;
    const selectedText = textarea.value.slice(start, end);
    const initialLatex = selectedText.startsWith('$') && selectedText.endsWith('$')
      ? selectedText.slice(1, -1)
      : selectedText;

    mathTargetRef.current = { textarea, onChange, start, end };
    setMathModalState({
      isOpen: true,
      initialLatex,
      anchorRect: textarea.getBoundingClientRect(),
    });
  }, []);

  const handleCloseMathEditor = useCallback(() => {
    setMathModalState({
      isOpen: false,
      initialLatex: '',
      anchorRect: null,
    });
    mathTargetRef.current = null;
  }, []);

  const handleInsertMath = useCallback((latex) => {
    const target = mathTargetRef.current;

    setMathModalState({
      isOpen: false,
      initialLatex: '',
      anchorRect: null,
    });

    if (!latex || !target?.textarea) {
      mathTargetRef.current = null;
      return;
    }

    const { textarea, start, end } = target;
    const insertion = `$${latex}$`;
    const nextValue = `${textarea.value.slice(0, start)}${insertion}${textarea.value.slice(end)}`;
    const nextCursor = start + insertion.length;

    textarea.value = nextValue;
    textarea.dispatchEvent(new Event('input', { bubbles: true }));

    window.setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
    }, 0);

    mathTargetRef.current = null;
  }, []);

  useEffect(() => {
    latestResponsesRef.current = responses;
  }, [responses]);

  const activateResponseBlocks = useCallback(() => {
    const container = pageContainerRef.current;
    if (!container || !isStarted || !pages.length) {
      return false;
    }

    container.querySelectorAll('[contenteditable="true"]').forEach((editable) => {
      editable.setAttribute('contenteditable', 'false');
    });

    let responseBlockIndex = 0;
    const blocks = Array.from(container.querySelectorAll('.response-area-block'));

    blocks.forEach((el) => {
      let blockId = el.getAttribute('data-response-id');
      if (!blockId || !responseAreas[blockId]) {
        blockId = responseAreaOrder[responseBlockIndex] || blockId || `response_${responseBlockIndex + 1}`;
      }
      responseBlockIndex += 1;

      const config = responseAreas[blockId] || buildResponseConfigFromBlock(el);
      if (!blockId || !config) return;

      el.setAttribute('data-response-id', blockId);
      el.removeAttribute('contenteditable');
      el.className = clsx(
        'response-area-block',
        isSubmitted ? 'response-area-disabled' : 'response-area-active'
      );
      const alreadyActivated = el.getAttribute('data-response-learner-active') === 'true';
      const stillHasEditorPreview = !!el.querySelector('.response-area-placeholder, .response-area-preview-title');
      if (!alreadyActivated || stillHasEditorPreview) {
        renderNativeResponseControl({
          host: el,
          blockId,
          config,
          value: latestResponsesRef.current[blockId]?.value ?? '',
          disabled: isSubmitted,
          enableModelAnswerToggle: canShowModelAnswerToggle,
          canAccessModelAnswers: isSignedIn,
          modelAnswerLockedMessage: 'Sign in to reveal model answers.',
          onChange: (nextValue) => handleResponseChange(blockId, nextValue),
          onOpenMath: handleOpenMathEditor,
        });
      } else {
        el.querySelectorAll('input, textarea, button').forEach((control) => {
          control.disabled = isSubmitted;
        });
      }
    });

    return blocks.length > 0;
  }, [
    handleOpenMathEditor,
    handleResponseChange,
    isStarted,
    isSubmitted,
    pages,
    responseAreaOrder,
    responseAreas,
  ]);

  // Same response activation path as shared self-practice, but retried and
  // observed so late DOM insertion cannot leave editor preview cards behind.
  useEffect(() => {
    if (!pageContainerRef.current || !isStarted || !pages.length) {
      return undefined;
    }

    const container = pageContainerRef.current;
    const timeouts = [];
    let frameId = 0;

    const run = () => {
      const activated = activateResponseBlocks();
      if (activated) {
        MathUtils.renderMath(container).catch((error) => {
          console.warn('Assessment math render failed:', error);
        });
      }
    };

    frameId = requestAnimationFrame(run);
    [50, 150, 400, 900, 1600].forEach((delay) => {
      timeouts.push(window.setTimeout(run, delay));
    });

    const observer = new MutationObserver(() => {
      run();
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frameId);
      timeouts.forEach((id) => window.clearTimeout(id));
      observer.disconnect();
    };
  }, [activateResponseBlocks, isStarted, pages]);

  // Auto-save debounce
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveResponses(), 2000);
  }, [saveResponses]);

  // Trigger auto-save when responses change
  useEffect(() => {
    if (!isStarted || isSubmitted) return;
    scheduleSave();
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [responses, isStarted, isSubmitted, scheduleSave]);

  // Timer
  useEffect(() => {
    if (!isStarted || isSubmitted || timeRemainingSeconds == null) return;
    timerIntervalRef.current = setInterval(() => tickTimer(), 1000);
    return () => clearInterval(timerIntervalRef.current);
  }, [isStarted, isSubmitted, timeRemainingSeconds != null, tickTimer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute server-based time remaining on load
  useEffect(() => {
    if (!currentSubmission || !assessment?.timeLimit || isSubmitted) return;
    const started = new Date(currentSubmission.startedAt);
    const elapsed = (Date.now() - started.getTime()) / 1000;
    const remaining = Math.max(0, assessment.timeLimit * 60 - elapsed);
    useAssessmentDocStore.setState({ timeRemainingSeconds: Math.round(remaining) });
  }, [currentSubmission?.startedAt, assessment?.timeLimit, isSubmitted]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = async () => {
    if (!user || !assessment) return;
    await startAssessment(
      documentId,
      {
        ...user,
        teacherAssignment: userData?.teacherAssignment || null,
        tutorAssignment: userData?.tutorAssignment || null,
      },
      assessment,
      {
        userId: currentDocument?.userId || null,
        ownerName: currentDocument?.ownerName || currentDocument?.userName || '',
        title: currentDocument?.title || currentDocument?.name || '',
        name: currentDocument?.name || currentDocument?.title || '',
      }
    );
  };

  const handleSubmit = async () => {
    setShowSubmitConfirm(false);
    await submitAssessment();
  };

  // Loading
  if (!currentDocument) {
    if (loadError) {
      return (
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="text-center">
            <p className="text-gray-700 font-medium mb-2">Assessment unavailable</p>
            <p className="text-sm text-gray-500 mb-4">{loadError}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-sigma-600 text-white hover:bg-sigma-700"
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (!assessment?.enabled) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-700 font-medium mb-2">Not an assessment</p>
          <p className="text-sm text-gray-500 mb-4">This document is not configured as an assessment.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-sigma-600 text-white hover:bg-sigma-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Start screen
  if (!isStarted) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <AssessmentDocStartScreen
          document={currentDocument}
          submission={currentSubmission}
          onStart={handleStart}
          isLoading={isLoading}
        />
      </div>
    );
  }

  const timerUrgent = timeRemainingSeconds != null && timeRemainingSeconds < 300;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top bar */}
      <div className="bg-sigma-800 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-30 shadow-md">
        <div className="flex items-center gap-4">
          <img src={logo} alt="Sigma Classroom" className="h-10 w-auto" />
          <div>
            <h1 className="text-base font-semibold">{currentDocument.name || 'Assessment'}</h1>
            <div className="text-xs text-sigma-200">
              {isSelfPractice
                ? 'Informal • Self Practice'
                : assessment?.category === 'informal'
                  ? 'Informal • Class Practice'
                  : 'Formal Assessment'}
            </div>
            <div className="text-xs text-sigma-200">
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'saved' && lastSavedAt && `Saved at ${lastSavedAt.toLocaleTimeString()}`}
              {saveStatus === 'error' && 'Save failed'}
              {saveStatus === 'idle' && lastSavedAt && 'Unsaved changes'}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Timer */}
          {timeRemainingSeconds != null && (
            <div className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-sm font-bold',
              timerUrgent ? 'bg-red-500/20 text-red-200 animate-pulse' : 'bg-white/10'
            )}>
              <Clock className="w-4 h-4" />
              {formatTime(timeRemainingSeconds)}
            </div>
          )}

          {!isSubmitted && (
            <>
              <button
                onClick={() => saveResponses()}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white/10 border border-white/20 hover:bg-white/20"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
              <button
                onClick={() => setShowSubmitConfirm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-green-500 text-white hover:bg-green-400"
              >
                <Send className="w-4 h-4" />
                Submit
              </button>
            </>
          )}

          {isSubmitted && (
            <div className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-green-500/20 text-green-200">
              <CheckCircle2 className="w-4 h-4" />
              Submitted
            </div>
          )}
        </div>
      </div>

      {/* Document pages (read-only) */}
      <div className="flex-1 overflow-y-auto py-10">
        {canShowModelAnswerToggle && (
          <div className="mx-auto mb-6 w-full max-w-[210mm] rounded-xl border border-emerald-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">Self Practice</div>
                <div className="text-xs text-gray-500">{modelAnswerHelperText}</div>
              </div>
            </div>
          </div>
        )}
        <AssessmentPages pages={pages} pageContainerRef={pageContainerRef} />
      </div>

      <InlineMathModal
        isOpen={mathModalState.isOpen}
        initialLatex={mathModalState.initialLatex}
        anchorRect={mathModalState.anchorRect}
        onInsert={handleInsertMath}
        onClose={handleCloseMathEditor}
      />

      {/* Submit confirmation */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-2xl border border-gray-200 p-6 text-center">
            <Send className="w-10 h-10 text-sigma-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Submit Assessment?</h3>
            <p className="text-sm text-gray-500 mb-5">
              Once submitted, you cannot change your answers.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-500"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentDocTaking;
